import React from 'react';
import { Modal, Button, message, Spin } from 'antd';
import { DownloadOutlined, FileOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useGetFileVersionsQuery } from '../../../redux/api/compassPortal';

const Section = ({ label, children, flagStyle }) => (
    <div className="cp-modal-section">
        <div className={`cp-modal-section__label ${flagStyle ? 'cp-modal-section__label--flag' : ''}`}>
            {flagStyle && '\u26A0 '}{label}
        </div>
        <div style={{
            background: 'var(--tertiary-color)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 14,
            color: 'var(--white-color)',
            minHeight: 38,
        }}>
            {children || <span style={{ color: 'var(--medium-light-grey-color)' }}>—</span>}
        </div>
    </div>
);

const DataSourceInfoModal = ({ open, onClose, source, projectId, groupName, metadataSchema = [] }) => {
    const [isDownloading, setIsDownloading] = React.useState(false);
    const [isDownloadingProcessed, setIsDownloadingProcessed] = React.useState(false);
    const token = useSelector((state) => state.oauth.auth0Token);
    const { data: fileVersions, isLoading: versionsLoading } = useGetFileVersionsQuery(
        { projectId, sourceId: source?.source_id },
        { skip: !source || !open },
    );

    if (!source) return null;

    const downloadFile = async (endpoint, filename, setLoading) => {
        setLoading(true);
        try {
            const baseUrl = process.env.REACT_APP_API_URL || '';
            const url = `${baseUrl}/compass_portal/projects/${projectId}/sources/${source.source_id}/${endpoint}`;
            const res = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`Download failed: ${res.status}`);
            const blob = await res.blob();
            const downloadUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = filename || 'download';
            a.click();
            URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error('Download failed:', err);
            message.error('File download failed');
        } finally {
            setLoading(false);
        }
    };

    const metricsDisplay = () => {
        if (!source.metrics) return null;
        const parts = [];
        if (source.metrics.checked?.length > 0) parts.push(source.metrics.checked.join(', '));
        if (source.metrics.open_value) parts.push(source.metrics.open_value);
        return parts.join('; ') || null;
    };

    // Build metadata display
    const metadata = source.metadata || {};

    return (
        <Modal
            title="Data Source Information"
            open={open}
            onCancel={onClose}
            footer={
                (source.file_url || source.processed_file_url) ? (
                    <div style={{ display: 'flex', gap: 8 }}>
                        {source.file_url && (
                            <Button icon={<DownloadOutlined />} loading={isDownloading} onClick={() => downloadFile('download', source.file_name, setIsDownloading)}>
                                {source.processed_file_url ? 'Download Original' : 'Download File'}
                            </Button>
                        )}
                        {source.processed_file_url && (
                            <Button type="primary" icon={<DownloadOutlined />} loading={isDownloadingProcessed} onClick={() => downloadFile('download_processed', source.processed_file_name, setIsDownloadingProcessed)}>
                                Download Processed
                            </Button>
                        )}
                    </div>
                ) : null
            }
            width={520}
            destroyOnClose
        >
            <Section label="Source Name">{source.name}</Section>
            <Section label="Data Group">{groupName || source.group_id}</Section>

            {metadataSchema.length > 0 && Object.keys(metadata).length > 0 && (
                <>
                    {metadataSchema.map((field) => {
                        const val = metadata[field.key];
                        if (val === undefined || val === null || val === '') return null;
                        return <Section key={field.key} label={field.label}>{String(val)}</Section>;
                    })}
                </>
            )}

            {source.processed_file_name && <Section label="Processed File">{source.processed_file_name}</Section>}
            {source.date_column && <Section label="Date Column">{source.date_column}</Section>}
            {source.breakdown && <Section label="Breakdown Columns">{source.breakdown}</Section>}
            {metricsDisplay() && <Section label="Metrics Included">{metricsDisplay()}</Section>}
            <Section label="Notes">{source.notes}</Section>
            {source.flags && <Section label="Flag" flagStyle>{source.flags}</Section>}

            {/* File Version History */}
            {(fileVersions && fileVersions.length > 0) && (
                <div style={{ marginTop: 16, borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--medium-light-grey-color)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                        File History
                    </div>
                    {fileVersions.map((v) => {
                        const date = v.uploaded_at?.seconds
                            ? new Date(v.uploaded_at.seconds * 1000)
                            : new Date(v.uploaded_at);
                        return (
                            <div key={v.version_id} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '6px 10px', marginBottom: 4,
                                background: 'var(--tertiary-color)', border: '1px solid rgba(255,255,255,0.05)',
                                borderRadius: 4, fontSize: 12,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <FileOutlined style={{ color: v.file_type === 'processed' ? '#a78bfa' : 'var(--green-color)' }} />
                                    <span style={{ color: 'var(--white-color)' }}>{v.file_name}</span>
                                    <span style={{ color: 'var(--medium-light-grey-color)' }}>
                                        v{v.version_number} &middot; {v.file_type} &middot; {v.uploaded_by}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ color: 'var(--medium-light-grey-color)', fontSize: 11 }}>
                                        {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </span>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<DownloadOutlined />}
                                        onClick={() => downloadFile(
                                            `file_versions/${v.version_id}/download`,
                                            v.file_name,
                                            setIsDownloading,
                                        )}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {versionsLoading && <Spin size="small" style={{ display: 'block', margin: '16px auto' }} />}
        </Modal>
    );
};

export default DataSourceInfoModal;
